// Types
import {
  AdminSearchExtTalents,
  AdminSearchExtTalentsVariables,
  AdminSearchExtTalents_adminSearchExtTalents_entities,
} from 'api/ExtTalent/types/adminSearchExtTalents';
import { GetPlaceDetails_getPlaceDetails } from 'api/places/types/GetPlaceDetails';
import { Sports_sports } from 'api/sports/types/Sports';
import {
  AdminSearchExtTalentsInput,
  ExtTalentAthleticLevel,
  ExtTalentContactRole,
  ExtTalentContactStatus,
  ExtTalentContactType,
  ExportExtTalentsInput,
  ExtTalentLinkStatus,
  ExtTalentLocationInput,
  ExtTalentExportColumn,
  ExtTalentExportFiltersInput,
  ExtTalentSocialPlatform,
  ExtTalentStatus,
  SortDirection,
  StoreGender,
} from 'api/graphql-global-types';
import { TableFilter } from 'ui/Table';
// Helpers
import { createSocialProfileUrlFromHandle } from './createSocialProfileUrlFromHandle';
import { checkIsUSorCA, joinLocation } from './place';

export type ExternalTalent =
  AdminSearchExtTalents_adminSearchExtTalents_entities;

export type ExternalTalentTableDisplayValues = {
  avgEngagement: number | null;
  bioHashtags: string;
  collegeUniversity: string;
  email: string;
  followersCount: number;
  hometown: string;
  league: string;
  location: string;
  name: string;
  profileNames: string;
  socialUrls: string;
  socialPlatforms: string;
  sport: string;
  team: string;
};

export type ExternalTalentTableRow = {
  athleticLevels: ExtTalentAthleticLevel | null;
  avgEngagement: number | null;
  bioHashtags: string;
  birthYear: number | null;
  collegeUniversity: string;
  email: string;
  followersCount: number;
  genders: StoreGender | null;
  hasEmail: 'false' | 'true';
  hometown: string;
  id: string;
  isOnPlatform: 'false' | 'true';
  league: string;
  location: string;
  name: string;
  original: AdminSearchExtTalents_adminSearchExtTalents_entities;
  profileNames: string;
  socialUrls: string;
  socialPlatforms: string;
  sport: string;
  team: string;
  updatedAt: string;
};

export type ContactFormValue = {
  agentName?: string;
  id?: string;
  role?: ExtTalentContactRole | null;
  type?: ExtTalentContactType;
  value?: string;
  status?: ExtTalentContactStatus;
};

export type SocialFormValue = {
  followersCount?: number | null;
  id?: string;
  platform?: ExtTalentSocialPlatform;
  profileName?: string;
  status?: ExtTalentLinkStatus;
  url?: string;
};

export type ExternalTalentFormValues = {
  athleticLevel?: ExtTalentAthleticLevel | null;
  birthDate?: number | null;
  birthMonth?: number | null;
  birthYear?: number | null;
  city?: string;
  citySearch?: string;
  collegeUniversity?: string;
  collegeUniversityRaw?: string;
  contacts?: ContactFormValue[];
  country?: string;
  firstName?: string;
  gender?: StoreGender | null;
  homeCity?: string;
  homeCountry?: string;
  homeLocationSearch?: string;
  homeState?: string;
  lastName?: string;
  league?: string;
  leagueRaw?: string;
  middleName?: string;
  socials?: SocialFormValue[];
  sport?: string;
  state?: string;
  status?: ExtTalentStatus;
  team?: string;
  teamRaw?: string;
};

export type PendingPlaceFields = Partial<
  Pick<
    ExternalTalentFormValues,
    | 'city'
    | 'citySearch'
    | 'country'
    | 'state'
    | 'homeCity'
    | 'homeCountry'
    | 'homeLocationSearch'
    | 'homeState'
  >
>;

export type LocationFilterPrefix = 'location' | 'hometown';

export type LocationFilterValues = {
  city: string;
  country: string;
  state: string;
};

export const currentYear = new Date().getFullYear();

export const birthYearOptions = Array.from(
  { length: currentYear - 1899 },
  (_, index) => currentYear - index
);

export const birthMonthOptions = Array.from(
  { length: 12 },
  (_, index) => index + 1
);

export const enumToLabel = (value?: string | null): string =>
  value
    ? value
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ')
    : '-';

export const normalizeString = (value?: string | null): string | null => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
};

export const normalizeRequiredString = (value?: string | null): string =>
  value?.trim() || '';

const normalizeSocialPathPart = (value?: string | null): string | null => {
  const normalizedValue = normalizeString(value);

  if (!normalizedValue) {
    return null;
  }

  return normalizedValue.replace(/^@+/, '').replace(/^\/+|\/+$/g, '') || null;
};

const SOCIAL_PLATFORM_HOSTS: Record<ExtTalentSocialPlatform, string[]> = {
  [ExtTalentSocialPlatform.facebook]: ['facebook.com', 'fb.com'],
  [ExtTalentSocialPlatform.instagram]: ['instagram.com'],
  [ExtTalentSocialPlatform.linkedin]: ['linkedin.com'],
  [ExtTalentSocialPlatform.tiktok]: ['tiktok.com'],
  [ExtTalentSocialPlatform.twitch]: ['twitch.tv'],
  [ExtTalentSocialPlatform.x]: ['x.com', 'twitter.com'],
  [ExtTalentSocialPlatform.youtube]: ['youtube.com'],
};

const SOCIAL_PLATFORM_RESERVED_SEGMENTS: Partial<
  Record<ExtTalentSocialPlatform, string[]>
> = {
  [ExtTalentSocialPlatform.facebook]: [
    'gaming',
    'groups',
    'marketplace',
    'profile.php',
    'reel',
    'reels',
    'share',
    'videos',
    'watch',
  ],
  [ExtTalentSocialPlatform.instagram]: [
    'accounts',
    'explore',
    'p',
    'reel',
    'reels',
    'stories',
    'tv',
  ],
  [ExtTalentSocialPlatform.twitch]: [
    'directory',
    'downloads',
    'jobs',
    'login',
    'messages',
    'settings',
    'signup',
    'subscriptions',
    'videos',
  ],
  [ExtTalentSocialPlatform.x]: [
    'explore',
    'home',
    'i',
    'intent',
    'login',
    'messages',
    'notifications',
    'search',
    'settings',
    'share',
  ],
};

const normalizeSocialHostname = (hostname: string): string =>
  hostname.toLowerCase().replace(/^www\./, '');

const matchesSocialPlatformHost = (
  hostname: string,
  platform: ExtTalentSocialPlatform
): boolean =>
  SOCIAL_PLATFORM_HOSTS[platform].some(
    (candidate) => hostname === candidate || hostname.endsWith(`.${candidate}`)
  );

const getSocialPlatformFromHostname = (
  hostname: string
): ExtTalentSocialPlatform | null =>
  (Object.values(ExtTalentSocialPlatform).find((platform) =>
    matchesSocialPlatformHost(hostname, platform)
  ) as ExtTalentSocialPlatform | undefined) || null;

const getSingleSegmentSocialHandle = (
  segments: string[],
  platform: ExtTalentSocialPlatform
): string | null => {
  if (segments.length !== 1) {
    return null;
  }

  const rawSegment = segments[0].toLowerCase();

  if (SOCIAL_PLATFORM_RESERVED_SEGMENTS[platform]?.includes(rawSegment)) {
    return null;
  }

  return normalizeSocialPathPart(segments[0]);
};

const getSocialHandleFromUrl = (
  parsedUrl: URL,
  platform: ExtTalentSocialPlatform
): string | null => {
  const normalizedHostname = normalizeSocialHostname(parsedUrl.hostname);

  if (!matchesSocialPlatformHost(normalizedHostname, platform)) {
    return null;
  }

  const segments = parsedUrl.pathname.split('/').filter(Boolean);

  switch (platform) {
    case ExtTalentSocialPlatform.linkedin:
      if (segments.length !== 2 || segments[0].toLowerCase() !== 'in') {
        return null;
      }

      return normalizeSocialPathPart(segments[1]);
    case ExtTalentSocialPlatform.tiktok:
    case ExtTalentSocialPlatform.youtube:
      if (segments.length !== 1 || !segments[0].startsWith('@')) {
        return null;
      }

      return normalizeSocialPathPart(segments[0]);
    case ExtTalentSocialPlatform.facebook:
    case ExtTalentSocialPlatform.instagram:
    case ExtTalentSocialPlatform.twitch:
    case ExtTalentSocialPlatform.x:
      return getSingleSegmentSocialHandle(segments, platform);
    default:
      return null;
  }
};

const getNormalizedSocialUrl = (value?: string | null): string | null => {
  const normalizedValue = normalizeString(value);

  if (!normalizedValue) {
    return null;
  }

  if (/^https?:\/\//i.test(normalizedValue)) {
    return normalizedValue;
  }

  if (normalizedValue.includes('/') || normalizedValue.includes('.')) {
    return `https://${normalizedValue.replace(/^\/+/, '')}`;
  }

  return null;
};

export const getExternalTalentSocialHandle = (
  value?: string | null,
  platform?: ExtTalentSocialPlatform | null
): string | null => {
  const normalizedValue = normalizeString(value);

  if (!normalizedValue) {
    return null;
  }

  const normalizedUrl = getNormalizedSocialUrl(normalizedValue);

  if (!normalizedUrl) {
    return normalizeSocialPathPart(normalizedValue);
  }

  try {
    const parsedUrl = new URL(normalizedUrl);
    const inferredPlatform =
      platform ||
      getSocialPlatformFromHostname(
        normalizeSocialHostname(parsedUrl.hostname)
      );

    if (!inferredPlatform) {
      return null;
    }

    return getSocialHandleFromUrl(parsedUrl, inferredPlatform);
  } catch (error) {
    return normalizeSocialPathPart(normalizedValue);
  }
};

export const getExternalTalentSocialUrl = (
  url?: string | null,
  platform?: ExtTalentSocialPlatform | null,
  handle?: string | null
): string | null => {
  const normalizedUrl = normalizeString(url);

  if (normalizedUrl) {
    return normalizedUrl;
  }

  const normalizedHandle = normalizeSocialPathPart(handle);

  if (!normalizedHandle || !platform) {
    return null;
  }

  return createSocialProfileUrlFromHandle(normalizedHandle, platform);
};

export const normalizeNumber = (value?: number | null): number | null =>
  typeof value === 'number' ? value : null;

export const isValidBirthDate = (
  year?: number | null,
  month?: number | null,
  day?: number | null
): boolean => {
  if (!year || !month || !day) {
    return true;
  }

  const testDate = new Date(year, month - 1, day);

  return (
    testDate.getFullYear() === year &&
    testDate.getMonth() === month - 1 &&
    testDate.getDate() === day
  );
};

export const getFileExtension = (file: File): string => {
  const extension = file.name.split('.').pop()?.toLowerCase();
  const allowedContentTypes = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/webp',
  ];

  if (!extension || !['png', 'jpg', 'jpeg', 'webp'].includes(extension)) {
    throw new Error('Avatar image must be png, jpg, jpeg, or webp');
  }

  if (file.type && !allowedContentTypes.includes(file.type)) {
    throw new Error('Avatar image must be a valid jpg, png, or webp file');
  }

  return extension;
};

export const getBirthDayCount = (
  year?: number | null,
  month?: number | null
): number => {
  if (!year || !month) {
    return 0;
  }

  return new Date(year, month, 0).getDate();
};

export const getExternalTalentSportName = (
  sportValue?: string | null,
  sports: Sports_sports[] = []
): string => {
  const normalizedSportValue = normalizeString(sportValue);

  if (!normalizedSportValue) {
    return '';
  }

  const matchingSport = sports.find(
    (sport) =>
      sport.name === normalizedSportValue ||
      sport.id.toString() === normalizedSportValue
  );

  return matchingSport?.name || normalizedSportValue;
};

export const getExternalTalentSportOptions = (
  sports: Sports_sports[] = [],
  currentSport?: string | null
): Sports_sports[] => {
  const normalizedCurrentSport = getExternalTalentSportName(
    currentSport,
    sports
  );

  if (
    !normalizedCurrentSport ||
    sports.some((sport) => sport.name === normalizedCurrentSport)
  ) {
    return sports;
  }

  return [
    ...sports,
    {
      __typename: 'UserSport',
      id: -1,
      name: normalizedCurrentSport,
    },
  ];
};

export const getExternalTalentLocationString = (
  city?: string | null,
  state?: string | null,
  country?: string | null
): string => joinLocation([city, state, country]);

export const getExternalTalentSocialUrlValues = (
  socials: ExternalTalent['socials'] = []
): string[] =>
  (socials || [])
    .map((social) =>
      getExternalTalentSocialUrl(social.url, social.platform, social.handle)
    )
    .filter((value): value is string => Boolean(value));

export const getExternalTalentTableDisplayValues = (
  talent: ExternalTalent,
  sports: Sports_sports[] = []
): ExternalTalentTableDisplayValues => {
  const socials = talent.socials || [];
  const contacts = talent.contacts || [];
  const hasMultiplePlatforms =
    new Set(socials.map((social) => social.platform).filter(Boolean)).size > 1;
  const email =
    contacts.find((contact) => contact.type === ExtTalentContactType.email)
      ?.value || '';
  const followersCount = socials.reduce(
    (sum, social) => sum + (social.followersCount || 0),
    0
  );
  const avgEngagementValues = socials
    .map((social) => social.extra?.avg_engagement)
    .filter((value): value is number => typeof value === 'number');
  const avgEngagement = avgEngagementValues.length
    ? avgEngagementValues.reduce((sum, value) => sum + value, 0) /
      avgEngagementValues.length
    : null;
  const formatSocialValue = (
    value?: string | null,
    platform?: ExtTalentSocialPlatform | null
  ): string | null => {
    if (!value) {
      return null;
    }

    if (!hasMultiplePlatforms || !platform) {
      return value;
    }

    return `${value} [${enumToLabel(platform)}]`;
  };

  return {
    avgEngagement,
    bioHashtags: socials
      .map((social) => social.extra?.bio_hashtags_concat)
      .filter(Boolean)
      .join(', '),
    collegeUniversity:
      talent.collegeUniversity || talent.extra?.college_university_raw || '',
    email,
    followersCount,
    hometown: getExternalTalentLocationString(
      talent.homeCity,
      talent.homeState,
      talent.homeCountry
    ),
    league: talent.league || talent.extra?.league_raw || '',
    location: getExternalTalentLocationString(
      talent.city,
      talent.state,
      talent.country
    ),
    name: [talent.firstName, talent.middleName, talent.lastName]
      .filter(Boolean)
      .join(' '),
    profileNames: socials
      .map((social) => formatSocialValue(social.profileName, social.platform))
      .filter(Boolean)
      .join(', '),
    socialUrls: getExternalTalentSocialUrlValues(socials).join(' '),
    socialPlatforms: Array.from(
      new Set(
        socials
          .map((social) => social.platform)
          .filter(Boolean)
          .map((platform) => enumToLabel(platform))
      )
    ).join(', '),
    sport: getExternalTalentSportName(talent.sport, sports),
    team: talent.team || talent.extra?.team_raw || '',
  };
};

export const getExtTalentPlaceFields = (
  placeDetails: GetPlaceDetails_getPlaceDetails,
  type: 'city' | 'home'
): Partial<ExternalTalentFormValues> => {
  const isUSOrCA = checkIsUSorCA(placeDetails.country);
  const state = isUSOrCA ? placeDetails.stateCode : null;
  const country = placeDetails.countryCode;
  const city = placeDetails.city;
  const searchValue = joinLocation([city, state, country]);

  if (type === 'home') {
    return {
      homeCity: city || '',
      homeCountry: country || '',
      homeLocationSearch: searchValue,
      homeState: state || undefined,
    };
  }

  return {
    city: city || '',
    citySearch: searchValue,
    country: country || '',
    state: state || undefined,
  };
};

export const getInitialValues = (
  talent?: ExternalTalent | null
): ExternalTalentFormValues => ({
  athleticLevel: talent?.athleticLevel || null,
  birthDate: talent?.birthDate || null,
  birthMonth: talent?.birthMonth || null,
  birthYear: talent?.birthYear || null,
  city: talent?.city || '',
  citySearch: joinLocation([talent?.city, talent?.state, talent?.country]),
  collegeUniversity: talent?.collegeUniversity || '',
  collegeUniversityRaw: talent?.extra?.college_university_raw || '',
  contacts:
    talent?.contacts?.map((contact) => ({
      agentName:
        (contact as { extra?: { agentName?: string | null } }).extra
          ?.agentName || '',
      id: contact.id,
      role: contact.role,
      status: contact.status,
      type: contact.type,
      value: contact.value,
    })) || [],
  country: talent?.country || '',
  firstName: talent?.firstName || '',
  gender: talent?.gender as StoreGender | null,
  homeCity: talent?.homeCity || '',
  homeCountry: talent?.homeCountry || '',
  homeLocationSearch: joinLocation([
    talent?.homeCity,
    talent?.homeState,
    talent?.homeCountry,
  ]),
  homeState: talent?.homeState || '',
  lastName: talent?.lastName || '',
  league: talent?.league || '',
  leagueRaw: talent?.extra?.league_raw || '',
  middleName: talent?.middleName || '',
  socials:
    talent?.socials?.map((social) => ({
      followersCount: social.followersCount,
      id: social.id,
      platform: social.platform,
      profileName: social.profileName || '',
      status: social.status,
      url:
        getExternalTalentSocialUrl(
          social.url,
          social.platform,
          social.handle
        ) || '',
    })) || [],
  sport: talent?.sport || '',
  state: talent?.state || '',
  status: talent?.status || ExtTalentStatus.active,
  team: talent?.team || '',
  teamRaw: talent?.extra?.team_raw || '',
});

type SearchFilterHandler = (value: TableFilter['value']) => void;

type ExternalTalentFiltersInput = ExtTalentExportFiltersInput;

const getRangeValue = (
  value: TableFilter['value'],
  defaultFrom: number,
  defaultTo: number
) => ({
  from: Number(Array.isArray(value) ? value[0] : value) || defaultFrom,
  to: Number(Array.isArray(value) ? value[1] : value) || defaultTo,
});

const setStringInputFilter = (
  input: ExternalTalentFiltersInput,
  field:
    | 'bioHashtags'
    | 'collegeUniversity'
    | 'league'
    | 'name'
    | 'profileName'
    | 'team'
): SearchFilterHandler => {
  return (value) => {
    input[field] = value as string;
  };
};

const setLocationFilter = (
  location: ExtTalentLocationInput,
  field: 'city' | 'country' | 'state'
): SearchFilterHandler => {
  return (value) => {
    location[field] = value as string;
  };
};

const getSearchFilterHandlers = ({
  hometownFilter,
  input,
  locationFilter,
}: {
  hometownFilter: ExtTalentLocationInput;
  input: ExternalTalentFiltersInput;
  locationFilter: ExtTalentLocationInput;
}): Record<string, SearchFilterHandler> => ({
  athleticLevels: (value) => {
    input.athleticLevels = value as ExtTalentAthleticLevel[];
  },
  avgEngagement: (value) => {
    input.avgEngagement = getRangeValue(value, 0, 0);
  },
  bioHashtags: setStringInputFilter(input, 'bioHashtags'),
  birthYear: (value) => {
    input.birthYear = getRangeValue(value, 1900, currentYear);
  },
  collegeUniversity: setStringInputFilter(input, 'collegeUniversity'),
  contactEmail: (value) => {
    input.contact = {
      type: ExtTalentContactType.email,
      value: value as string,
    };
  },
  followersCount: (value) => {
    input.followersCount = getRangeValue(value, 0, 0);
  },
  genders: (value) => {
    input.genders = value as StoreGender[];
  },
  hasEmail: (value) => {
    input.hasEmail = value === 'true';
  },
  hometownCity: setLocationFilter(hometownFilter, 'city'),
  hometownCountry: setLocationFilter(hometownFilter, 'country'),
  hometownState: setLocationFilter(hometownFilter, 'state'),
  isOnPlatform: (value) => {
    input.isOnPlatform = value === 'true';
  },
  league: setStringInputFilter(input, 'league'),
  locationCity: setLocationFilter(locationFilter, 'city'),
  locationCountry: setLocationFilter(locationFilter, 'country'),
  locationState: setLocationFilter(locationFilter, 'state'),
  name: setStringInputFilter(input, 'name'),
  profileName: setStringInputFilter(input, 'profileName'),
  socialUrl: (value) => {
    input.socialHandle = getExternalTalentSocialHandle(value as string);
  },
  socialPlatform: (value) => {
    input.socialPlatform = value as ExtTalentSocialPlatform;
  },
  sports: (value) => {
    input.sports = value ? [value as string] : null;
  },
  team: setStringInputFilter(input, 'team'),
});

export const getExternalTalentFiltersInput = ({
  appliedQuery,
  searchFilters,
}: {
  appliedQuery: string;
  searchFilters: TableFilter[];
}): ExtTalentExportFiltersInput => {
  const locationFilter: ExtTalentLocationInput = {};
  const hometownFilter: ExtTalentLocationInput = {};
  const input: ExtTalentExportFiltersInput = {};
  const filterHandlers = getSearchFilterHandlers({
    hometownFilter,
    input,
    locationFilter,
  });

  if (appliedQuery) {
    input.query = appliedQuery;
  }

  searchFilters.forEach(({ key, value }) => {
    filterHandlers[key]?.(value);
  });

  if (locationFilter.city || locationFilter.state || locationFilter.country) {
    input.locations = [locationFilter];
  }

  if (hometownFilter.city || hometownFilter.state || hometownFilter.country) {
    input.homeLocations = [hometownFilter];
  }

  return input;
};

export const getSearchVariables = ({
  appliedQuery,
  currentPage,
  pageSize,
  searchFilters,
}: {
  appliedQuery: string;
  currentPage: number;
  pageSize: number;
  searchFilters: TableFilter[];
}): AdminSearchExtTalentsVariables => {
  const filtersInput = getExternalTalentFiltersInput({
    appliedQuery,
    searchFilters,
  });
  const input: AdminSearchExtTalentsInput = {
    ...filtersInput,
    direction: SortDirection.DESC,
    limit: pageSize,
    offset: (currentPage > 1 ? currentPage - 1 : 0) * pageSize,
    query: filtersInput.query || null,
  };

  return { input };
};

export const getExternalTalentExportInput = ({
  appliedQuery,
  columns,
  searchFilters,
}: {
  appliedQuery: string;
  columns: ExtTalentExportColumn[];
  searchFilters: TableFilter[];
}): ExportExtTalentsInput => {
  const filters = getExternalTalentFiltersInput({
    appliedQuery,
    searchFilters,
  });

  return {
    ...(columns.length ? { columns } : {}),
    ...(Object.keys(filters).length ? { filters } : {}),
  };
};

export const getFilterValue = (
  searchFilters: TableFilter[],
  key: string
): string =>
  (searchFilters.find((filter) => filter.key === key)?.value as string) || '';

export const getLocationFilterValues = (
  searchFilters: TableFilter[],
  prefix: LocationFilterPrefix
): LocationFilterValues => ({
  city: getFilterValue(searchFilters, `${prefix}City`),
  country: getFilterValue(searchFilters, `${prefix}Country`),
  state: getFilterValue(searchFilters, `${prefix}State`),
});

export const hasLocationFilters = (
  searchFilters: TableFilter[],
  prefix: LocationFilterPrefix
): boolean => {
  const values = getLocationFilterValues(searchFilters, prefix);

  return Boolean(values.city || values.country || values.state);
};

export const getLocationFilterKeys = (
  searchFilters: TableFilter[],
  prefix: LocationFilterPrefix
): string[] | null => {
  const values = getLocationFilterValues(searchFilters, prefix);
  const selectedKeys = [values.country, values.state, values.city];

  return selectedKeys.some(Boolean) ? selectedKeys : null;
};

export const updateLocationFilters = (
  searchFilters: TableFilter[],
  prefix: LocationFilterPrefix,
  values: LocationFilterValues
): TableFilter[] => {
  const nextFilters = searchFilters.filter(
    (filter) =>
      ![`${prefix}Country`, `${prefix}State`, `${prefix}City`].includes(
        filter.key
      )
  );

  if (values.country.trim()) {
    nextFilters.push({
      key: `${prefix}Country`,
      value: values.country.trim(),
    });
  }

  if (values.state.trim()) {
    nextFilters.push({
      key: `${prefix}State`,
      value: values.state.trim(),
    });
  }

  if (values.city.trim()) {
    nextFilters.push({
      key: `${prefix}City`,
      value: values.city.trim(),
    });
  }

  return nextFilters;
};

export const clearLocationFilters = (
  searchFilters: TableFilter[],
  prefix: LocationFilterPrefix
): TableFilter[] =>
  searchFilters.filter(
    (filter) =>
      ![`${prefix}Country`, `${prefix}State`, `${prefix}City`].includes(
        filter.key
      )
  );

export const updateSingleFilter = (
  searchFilters: TableFilter[],
  key: string,
  value?: string
): TableFilter[] => {
  const nextFilters = searchFilters.filter((filter) => filter.key !== key);

  if (value) {
    nextFilters.push({
      key,
      value,
    });
  }

  return nextFilters;
};

export const getSportFilterValue = (searchFilters: TableFilter[]): string =>
  getFilterValue(searchFilters, 'sports');

export const getExternalTalentTableRows = (
  talents: AdminSearchExtTalents['adminSearchExtTalents']['entities'] = [],
  sports: Sports_sports[] = []
): ExternalTalentTableRow[] =>
  (talents || []).map((talent) => {
    const displayValues = getExternalTalentTableDisplayValues(talent, sports);

    return {
      athleticLevels: talent.athleticLevel,
      avgEngagement: displayValues.avgEngagement,
      bioHashtags: displayValues.bioHashtags,
      birthYear: talent.birthYear,
      collegeUniversity: displayValues.collegeUniversity,
      email: displayValues.email,
      followersCount: displayValues.followersCount,
      genders: talent.gender as StoreGender | null,
      hasEmail: displayValues.email ? 'true' : 'false',
      hometown: displayValues.hometown,
      id: talent.id,
      isOnPlatform: talent.userId ? 'true' : 'false',
      league: displayValues.league,
      location: displayValues.location,
      name: displayValues.name,
      original: talent,
      profileNames: displayValues.profileNames,
      socialUrls: displayValues.socialUrls,
      socialPlatforms: displayValues.socialPlatforms,
      sport: displayValues.sport,
      team: displayValues.team,
      updatedAt: talent.updatedAt,
    };
  });
